import { ReactNode, useRef, useState, useCallback, useEffect } from 'react'
import { clsx } from 'clsx'
import type { Message } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import MessageContextMenu from './MessageContextMenu'

interface MessageSwipeWrapperProps {
  message: Message
  children: ReactNode
  isLastUserMessage?: boolean
}

// Swipe configuration
const SWIPE_THRESHOLD = 60  // Minimum swipe distance to trigger
const MAX_SWIPE = 100       // Maximum visual translation
const REPLY_ICON_OPACITY_START = 30  // Start showing icon at this distance
const LONG_PRESS_DELAY = 500  // ms

/**
 * Wrapper component that adds:
 * - Swipe-right-to-reply gesture
 * - Long-press/right-click context menu
 */
export default function MessageSwipeWrapper({
  message,
  children,
  isLastUserMessage = false,
}: MessageSwipeWrapperProps) {
  const setReplyingTo = useChatStore((state) => state.setReplyingTo)
  const [swipeX, setSwipeX] = useState(0)
  const swipeXRef = useRef(0) // Ref mirror for threshold check in touch end
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)
  const isSwipingRef = useRef(false)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => clearLongPress()
  }, [clearLongPress])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() }
    isSwipingRef.current = false

    // Start long press timer
    longPressTimerRef.current = setTimeout(() => {
      if (touchStartRef.current) {
        setContextMenu({ x: touchStartRef.current.x, y: touchStartRef.current.y })
      }
    }, LONG_PRESS_DELAY)
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return

    const touch = e.touches[0]
    const deltaX = touch.clientX - touchStartRef.current.x
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)

    // Cancel long press on any movement
    clearLongPress()

    // If vertical movement is larger, it's a scroll - cancel swipe
    if (deltaY > 30 && !isSwipingRef.current) {
      touchStartRef.current = null
      swipeXRef.current = 0
      setSwipeX(0)
      return
    }

    // Only allow right swipe (positive deltaX)
    if (deltaX > 10) {
      isSwipingRef.current = true
      const clampedSwipe = Math.min(deltaX, MAX_SWIPE)
      swipeXRef.current = clampedSwipe
      setSwipeX(clampedSwipe)
    }
  }, [clearLongPress])

  const handleTouchEnd = useCallback(() => {
    clearLongPress()

    // Use ref for threshold check (state may be stale on fast swipes)
    if (swipeXRef.current >= SWIPE_THRESHOLD) {
      // Trigger reply
      setReplyingTo(message)
    }
    // Reset swipe state
    swipeXRef.current = 0
    setSwipeX(0)
    touchStartRef.current = null
    isSwipingRef.current = false
  }, [message, setReplyingTo, clearLongPress])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY })
  }, [])

  const closeContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  // Calculate reply icon opacity based on swipe distance
  const iconOpacity = Math.min(
    1,
    Math.max(0, (swipeX - REPLY_ICON_OPACITY_START) / (SWIPE_THRESHOLD - REPLY_ICON_OPACITY_START))
  )

  const isTriggered = swipeX >= SWIPE_THRESHOLD

  return (
    <div className="relative overflow-hidden">
      {/* Reply icon (appears from left during swipe) */}
      <div
        className={clsx(
          'absolute left-2 top-1/2 -translate-y-1/2 transition-opacity',
          isTriggered ? 'text-primary-500' : 'text-neutral-400'
        )}
        style={{ opacity: iconOpacity }}
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
          />
        </svg>
      </div>

      {/* Message content with swipe transform */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swipeX === 0 ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {children}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <MessageContextMenu
          message={message}
          position={contextMenu}
          onClose={closeContextMenu}
          isLastUserMessage={isLastUserMessage}
        />
      )}
    </div>
  )
}
